import type { ManifestRecording, RecordingsManifest } from '../shared/manifest'
import { MANIFEST_KEY } from '../shared/manifest'
import type { Recording } from './constants'
import { MEDIA_VERSION, VIDEO_ORIGIN } from './media'

/**
 * The original mosaic ships with the app rather than living in the manifest:
 * its audio is bundled with the Pages deployment (small enough, and versioned
 * by MEDIA_VERSION), only its video lives in R2, and it must be playable even
 * if the manifest fetch fails. Its numbers are the measured facts documented
 * in constants.ts.
 */
function builtinRecording(): Recording {
  const stems = [
    '01_clave',
    '02_cata',
    '03_erikundi',
    '04_quinto',
    '05_conga',
    '06_tumba',
    '07_coro_1',
    '08_coro_2',
  ]
  const local = (path: string) => `${path}?v=${MEDIA_VERSION}`
  const remote = (file: string) => `${VIDEO_ORIGIN}/${file}?v=${MEDIA_VERSION}`
  const bank = (tempo: number, format: string) => ({
    format,
    urls: stems.map((id) => local(`media/audio/${tempo}/${id}.${format}`)),
  })
  const tiers = (base: string, width: number, height: number): Recording['videoFull'] => [
    // HEVC leads on both size and Apple hardware coverage; AV1 covers browsers
    // without HEVC; H.264 is the universal floor. All are driven by the same
    // audio clock, so which one a browser lands on has no bearing on sync.
    { url: remote(`${base}.hevc.mp4`), width, height, codec: 'hvc1.1.6.L153.B0', requireHardware: width >= 3840 },
    { url: remote(`${base}.av1.mp4`), width, height, codec: 'av01.0.09M.08', requireHardware: width >= 3840 },
    { url: remote(`${base}.h264.mp4`), width, height, codec: 'avc1.640034', requireHardware: false },
  ]

  return {
    id: 'mosaic',
    title: 'Mosaic — Afro-Cuban percussion',
    sampleRate: 48_000,
    loopSamples: 1_148_160,
    mediaSeconds: 24.05,
    videoFps: 60,
    cols: 4,
    rows: 2,
    trackNames: ['Clave', 'Cata', 'Erikundi', 'Quinto', 'Conga', 'Tumba', 'Coro 1', 'Coro 2'],
    tempoPresets: [25, 50, 75, 100],
    audio: {
      // 100% is FLAC (lossless, no encoder priming delay); the others keep
      // their original AAC bytes. See scripts/build-media.mjs.
      25: bank(25, 'm4a'),
      50: bank(50, 'm4a'),
      75: bank(75, 'm4a'),
      100: bank(100, 'flac'),
    },
    videoFull: tiers('mosaic_4x2', 3840, 1920),
    videoHalf: tiers('mosaic_4x2_half', 1920, 960),
    posterUrl: local('media/mosaic_poster.jpg'),
  }
}

/** Uploaded media keys are unique per recording id, so they cache immutably. */
const bucketUrl = (key: string) => `${VIDEO_ORIGIN}/${key.split('/').map(encodeURIComponent).join('/')}`

function resolveManifestRecording(m: ManifestRecording): Recording {
  const audio: Recording['audio'] = {}
  for (const tempo of m.tempoPresets) {
    const bank = m.audio[String(tempo)]
    if (!bank) continue
    audio[tempo] = { format: bank.format, urls: bank.keys.map(bucketUrl) }
  }
  const tier = (t: ManifestRecording['video']['full'][number]) => ({
    url: bucketUrl(t.key),
    width: t.width,
    height: t.height,
    codec: t.codec,
    requireHardware: t.requireHardware,
  })
  return {
    id: m.id,
    title: m.title,
    sampleRate: m.sampleRate,
    loopSamples: m.loopSamples,
    mediaSeconds: m.mediaSeconds,
    videoFps: m.videoFps,
    cols: m.cols,
    rows: m.rows,
    trackNames: m.trackNames,
    tempoPresets: [...m.tempoPresets].sort((a, b) => a - b),
    audio,
    videoFull: m.video.full.map(tier),
    videoHalf: (m.video.half ?? []).map(tier),
    posterUrl: m.posterKey ? bucketUrl(m.posterKey) : null,
  }
}

/**
 * The built-in mosaic plus whatever the manifest in the bucket describes.
 * A missing or unreachable manifest is not an error — the player simply has
 * one recording, exactly as before the manifest existed.
 */
export async function loadRecordings(): Promise<Recording[]> {
  const list: Recording[] = [builtinRecording()]
  try {
    const res = await fetch(`${VIDEO_ORIGIN}/${MANIFEST_KEY}`, { cache: 'no-cache' })
    if (res.ok) {
      const manifest = (await res.json()) as RecordingsManifest
      for (const entry of manifest.recordings ?? []) {
        if (entry.id === 'mosaic') continue
        try {
          list.push(resolveManifestRecording(entry))
        } catch {
          // One malformed entry must not take down the rest.
        }
      }
    }
  } catch {
    // Offline, or no manifest yet.
  }
  return list
}
